import * as React from 'react';
import { cn } from '@/lib/utils';

export interface VerticalGradientLineProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** ラインの太さ（px） */
  thickness?: number;
  /** ラインの高さ（px または %） */
  height?: string | number;
  /** グラデーションの開始色 */
  startColor?: string;
  /** グラデーションの終了色 */
  endColor?: string;
  /** グラデーションの角度（deg） */
  angle?: number;
}

/**
 * VerticalGradientLine - 縦方向グラデーションラインコンポーネント
 *
 * ピンクから水色へのグラデーションが適用された縦方向の装飾ラインを表示します。
 * テキストの横やセクション区切りとして使用します。
 *
 * @example
 * ```tsx
 * // デフォルト
 * <VerticalGradientLine />
 *
 * // サイズを指定
 * <VerticalGradientLine height={50} thickness={2} />
 *
 * // 色をカスタマイズ
 * <VerticalGradientLine startColor="#FFA6CB" endColor="#86F1FF" />
 *
 * // 角度を変更
 * <VerticalGradientLine angle={90} />
 *
 * // Tailwindのクラスで追加スタイル
 * <VerticalGradientLine className="ml-4" />
 * ```
 */
export const VerticalGradientLine = React.forwardRef<
  HTMLDivElement,
  VerticalGradientLineProps
>(
  (
    {
      className,
      thickness = 1,
      height = '100%',
      startColor = '#FFA6CB',
      endColor = '#86F1FF',
      angle = 119,
      style,
      ...props
    },
    ref,
  ) => {
    return (
      <div
        ref={ref}
        className={cn('block', className)}
        style={{
          width: thickness,
          height: typeof height === 'number' ? `${height}px` : height,
          background: `linear-gradient(${angle}deg, ${startColor} 14.22%, ${endColor} 91.18%)`,
          ...style,
        }}
        {...props}
      />
    );
  },
);

VerticalGradientLine.displayName = 'VerticalGradientLine';
